import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Check, X } from "lucide-react";

/**
 * A dropdown bound to a managed Stammdaten list with inline "add new" support.
 */
export default function ManagedSelect({
  label,
  testId,
  placeholder = "Auswählen…",
  value,
  onChange,
  options = [],
  onAdd,
}) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const v = draft.trim();
    if (!v) return;
    setBusy(true);
    try {
      // Select the value first (parent also optimistically inserts the option,
      // so the matching SelectItem exists in the same render batch).
      onChange(v);
      await onAdd(v);
      setDraft("");
      setAdding(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <label className="data-label block">{label}</label>
      <div className="flex gap-2">
        <Select
          value={value || ""}
          onValueChange={(v) => {
            // Radix can emit a spurious empty onValueChange when the options
            // list reference changes (e.g. after adding a new entry). There is
            // no UI to clear a selection, so ignore empty callbacks.
            if (v) onChange(v);
          }}
        >
          <SelectTrigger data-testid={testId} className="bg-white flex-1">
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {options.length === 0 ? (
              <div className="px-2 py-1.5 text-sm text-muted-foreground">
                Keine Einträge
              </div>
            ) : (
              options.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.value}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="outline"
          size="icon"
          tabIndex={-1}
          onClick={() => setAdding((a) => !a)}
          data-testid={`${testId}-add-toggle`}
          title="Neuen Eintrag hinzufügen"
          className="shrink-0"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {adding && (
        <div className="flex gap-2 pt-1 animate-fade-up">
          <Input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Neuer Eintrag…"
            data-testid={`${testId}-new-input`}
            className="bg-white"
          />
          <Button
            type="button"
            size="icon"
            tabIndex={-1}
            onClick={submit}
            disabled={busy}
            data-testid={`${testId}-new-confirm`}
            className="shrink-0"
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            tabIndex={-1}
            onClick={() => {
              setAdding(false);
              setDraft("");
            }}
            className="shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
